/**
 * External Imports
 */
import { FC, useState, SetStateAction, Dispatch, useMemo } from 'react';
import { parse, format } from 'date-fns';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { toTitleCase } from '../../../../utils/helpers';
import { useLazyGetPatientRoundHistoryQuery } from '../../../../redux/apis/Reports.api';

/**
 * Global Type Definition Imports
 */
import { Encounter } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface EncountersTableProps {
  encountersList?: Encounter[];
  setEncounterHistory: Dispatch<SetStateAction<Encounter | null>>;
  setShowHistory: Dispatch<SetStateAction<boolean>>;
}
const EncountersTable: FC<EncountersTableProps> = ({
  encountersList = [],
  setEncounterHistory,
  setShowHistory
}) => {
  const [sortKey, setSortKey] = useState<string>('admittime');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Admit Date');

  const [patientRoundHistory] = useLazyGetPatientRoundHistoryQuery();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Admit Date':
        key = 'admittime';
        break;
      case 'Patient Name':
        key = 'firstname';
        break;
      case 'MRN':
        key = 'mrn';
        break;
      case 'Account':
        key = 'accountnumber';
        break;
      case 'Patient Type':
        key = 'patienttype';
        break;
      case 'Unit':
        key = 'pointofcare';
        break;
      case 'Room':
        key = 'room';
        break;
      case 'Bed':
        key = 'bed';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const handleGetHistory = (encounter: Encounter) => {
    patientRoundHistory({
      facilityId: encounter.facility,
      patientId: encounter.urn
    });
    setEncounterHistory(encounter);
    setShowHistory(true);
  };

  const sortedEncountersList = useMemo(() => {
    const stateCopy = [...encountersList];
    const sorted = stateCopy.sort((a: Encounter, b: Encounter) => {
      if (a[sortKey] === b[sortKey]) {
        return 0;
      }
      if (!a[sortKey]) {
        return sortDir === 'asc' ? 1 : -1;
      }
      if (!b[sortKey]) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (sortDir === 'asc') {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! > b[sortKey]! ? -1 : 1;
    });
    return sorted;
  }, [encountersList, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => sortedEncountersList.length === 0,
    [sortedEncountersList]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Admit Date"
      id="Encounter-Table-Admit-Column"
      unit="column"
      colWidth={16}
      direction={!(active === 'Admit Date') ? 'none' : sortDir}
      sort
    >
      Admit Date
    </CellContainer>,
    <CellContainer
      key="Patient Name"
      id="Encounter-Table-Patient-Name-Column"
      unit="column"
      colWidth={16}
      direction={!(active === 'Patient Name') ? 'none' : sortDir}
      sort
    >
      Patient Name
    </CellContainer>,
    <CellContainer
      key="MRN"
      id="Encounter-Table-MRN-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'MRN') ? 'none' : sortDir}
      sort
    >
      MRN
    </CellContainer>,
    <CellContainer
      key="Account"
      id="Encounter-Table-Account-Column"
      unit="column"
      colWidth={13}
      direction={!(active === 'Account') ? 'none' : sortDir}
      sort
    >
      Account
    </CellContainer>,
    <CellContainer
      key="Patient Type"
      id="Encounter-Table-Patient-Type-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Patient Type') ? 'none' : sortDir}
      sort
    >
      Patient Type
    </CellContainer>,
    <CellContainer
      key="Unit"
      id="Encounter-Table-Unit-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Unit') ? 'none' : sortDir}
      sort
    >
      Unit
    </CellContainer>,
    <CellContainer
      key="Room"
      id="Encounter-Table-Room-Column"
      unit="column"
      colWidth={8}
      direction={!(active === 'Room') ? 'none' : sortDir}
      sort
    >
      Room
    </CellContainer>,
    <CellContainer
      key="Bed"
      id="Encounter-Table-Bed-Column"
      unit="column"
      colWidth={8}
      direction={!(active === 'Bed') ? 'none' : sortDir}
      sort
    >
      Bed
    </CellContainer>
  ];

  const rows = sortedEncountersList.map((encounter: Encounter, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Encounter-Row-${i}`}
        id={`Encounter-Row-${encounter.msgid}`}
        unit="row"
        height="5.5em"
        role="button"
        size="default"
        onClick={() => {
          handleGetHistory(encounter);
        }}
      >
        <CellContainer unit="column" colWidth={16}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {encounter?.admittime?.length === 14
              ? format(
                  parse(encounter?.admittime, 'yyyyMMddHHmmss', new Date()),
                  'Pp'
                ).replace(',', ' ')
              : encounter?.admittime?.length === 12
              ? format(
                  parse(encounter?.admittime, 'yyyyMMddHHmm', new Date()),
                  'Pp'
                ).replace(',', ' ')
              : encounter?.admittime?.length === 8
              ? format(
                  parse(encounter?.admittime, 'yyyyMMdd', new Date()),
                  'P'
                ).replace(',', ' ')
              : null}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={16}>
          <StyledLabel>
            {toTitleCase(`${encounter.firstname} ${encounter.lastname}`)}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <StyledLabel>{encounter.mrn}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={13}>
          <StyledLabel>{encounter.accountnumber}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel>{encounter.patienttype}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>{encounter.pointofcare}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={8}>
          <StyledLabel>{encounter.room ? encounter.room : 'N/A'}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={8}>
          <StyledLabel>{encounter.bed ? encounter.bed : 'N/A'}</StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="encounter-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      type="encounters"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default EncountersTable;
